/**
 * Parseur / normaliseur canonique des **citations de pièces** dans un acte juridique FR.
 *
 * Une « citation de pièce » est le renvoi que l'avocat écrit AU FIL DU TEXTE ou sur une ligne
 * dédiée pour désigner une ou plusieurs pièces de son bordereau : `Pièce 1`, `Pièce n°1`,
 * `Pièces n° 1, 2 et 3`, `Pièce N°1 & 2`, `Pièces 1 à 5`…
 *
 * WHY (2026-09-03) — la reconnaissance de ces renvois était éclatée en **six** grammaires
 * divergentes, chacune ne couvrant qu'une tranche des formes réelles :
 *  - `marqueurs.ts` `isPiecesLine` (10 regexes, `n°` obligatoire sauf pattern 10, mono-numéro) —
 *    mise en forme Word/canvas ;
 *  - `apps/addin-word/src/services/wordInsertion.ts` (copie figée des 9 premiers patterns) ;
 *  - `ExtractLegalReferences.ts` `PIECE_PATTERNS` (`n°`/`numéro` OBLIGATOIRE, mono-numéro) —
 *    extraction des renvois d'un acte ;
 *  - `pieceCitationMetrics.ts` `PIECE_RE` (le plus complet : listes + plages, mais pas `&`,
 *    pas `nº`, pas `numéro`, pas de signe répété `n°1 & n°2`) — mesure de la convention cabinet ;
 *  - `pdf_piece_matching_text.ts` (détection de signal `Pièce n°` pour arbitrer OCR vs texte natif) ;
 *  - `case_data_loader.ts` / `ReferenceActDocxStyleExtractor.ts` (nettoyage de préfixe).
 * Conséquence mesurée : `Pièce N°1 & 2` et `Pièces 1 à 5` n'étaient reconnus par AUCUN des
 * consommateurs « lien / re-numérotation / mise en forme », et `Pièce 1` nu n'était vu que par
 * deux d'entre eux. Ce module est la grammaire unique, pure et testée.
 *
 * **Le vrai risque est le FAUX POSITIF** : « pièce d'identité », « pièce jointe »,
 * « pièce maîtresse », « pièce à conviction » sont du français courant, pas des renvois. La
 * grammaire est donc **fermée** : après le mot `pièce(s)`, seul un *qualificatif connu*
 * (`adverse`, `jointe`, `annexe`, `communiquée`, `versée`, `produite`), un *signe de numéro*
 * (`n°`, `nº`, `no`, `numéro`, `#`) ou *directement un chiffre* peut suivre. Tout autre mot
 * (`d'identité`, `maîtresse`, `à conviction`…) arrête la reconnaissance. Voir la table de vérité
 * dans `pieceCitation.test.ts`.
 *
 * Hub : `docs/features/piece-citation/README.md`.
 *
 * @module
 */

/** Suffixes juridiques FR admis derrière un numéro de pièce (alignés sur `@hector/piece-number-sort`). */
const FR_SUFFIXES = [
  'bis',
  'ter',
  'quater',
  'quinquies',
  'sexies',
  'septies',
  'octies',
  'nonies',
  'decies',
] as const;

/**
 * Qualificatifs admis entre `pièce(s)` et le numéro.
 *
 * WHY : cette liste FERMÉE est la garde anti-faux-positif n°1. Elle interdit à
 * « pièce d'identité », « pièce maîtresse », « pièce à conviction » de devenir des citations,
 * là où une regex ouverte du genre `Pièce\s+([A-Za-zÀ-ÿ\s]+)\s*:` (pattern 4 de `isPiecesLine`)
 * les avale. Toute nouvelle forme rencontrée en prod s'ajoute ICI, jamais en élargissant la classe.
 */
const QUALIFIERS: readonly { readonly re: RegExp; readonly kind: PieceCitationQualifier }[] = [
  { re: /^adverses?(?![\p{L}])/iu, kind: 'adverse' },
  // `adv.` — abréviation relevée 15 fois dans le corpus (« Pièce adv. 15) »). Le point est
  // EXIGÉ : `adv` nu serait le début d'un mot que rien ne borne.
  { re: /^adv\.(?![\p{L}])/iu, kind: 'adverse' },
  { re: /^jointes?(?![\p{L}])/iu, kind: 'jointe' },
  { re: /^annexes?(?![\p{L}])/iu, kind: 'annexe' },
  { re: /^communiqu[ée]es?(?![\p{L}])/iu, kind: 'communiquee' },
  // « PIÈCE COMMUNE 8 » — pièce versée par plusieurs parties, 23 occurrences relevées.
  // Placé APRÈS `communiquée` : les deux commencent par `commun`, et l'ordre décide.
  { re: /^communes?(?![\p{L}])/iu, kind: 'commune' },
  { re: /^vers[ée]es?(?![\p{L}])/iu, kind: 'versee' },
  { re: /^produites?(?![\p{L}])/iu, kind: 'produite' },
];

/** Nature du qualificatif porté par la citation (`Pièce adverse n°3`). */
export type PieceCitationQualifier =
  | 'adverse'
  | 'jointe'
  | 'annexe'
  | 'communiquee'
  | 'commune'
  | 'versee'
  | 'produite';

/** Forme du signe de numéro écrite par l'avocat. `null` = citation « nue » (`Pièce 1`). */
export type PieceNumberSign = 'degre' | 'no' | 'numero' | 'diese';

/**
 * Mot-clé de tête : `pièce` / `pièces` / `PIÈCE` / `PIECES`, isolé (jamais à l'intérieur d'un mot).
 *
 * Les lookarounds `\p{L}` (flag `u`) remplacent `\b`, INUTILISABLE ici : en regex JS non-Unicode,
 * `è` n'est pas un caractère de mot, donc `\b` place une frontière AU MILIEU de « Pièce ».
 * `\*{0,2}` absorbe le gras markdown que le LLM produit régulièrement (`**Pièce n°1**`).
 */
const HEAD_RE = /(?<![\p{L}\p{N}])(\*{0,2})(pi[eè]ce)(s)?(?![\p{L}])/giu;

/**
 * Signe de numéro. `°` = U+00B0 (degré), `º` = U+00BA (ordinal masculin — ce que produisent
 * beaucoup de PDF et le clavier macOS). `no`/`nos`/`n.` tolérés sans signe de degré.
 * `n` NU est refusé : le `o` ou le point est le seul indice qui distingue un signe de numéro
 * d'une initiale de nom propre.
 *
 * Le chiffre qui suit est EXIGÉ par l'appelant (`readNumber`) : c'est ce qui empêche
 * « Pièce non communiquée » de matcher sur `no`.
 */
const SIGN_RE =
  /^(?:(n\s*[°º])|(n\.|nos?\.?)|(num[ée]rot[ée]{1,2}s?)|(num[ée]ros?)|(#)|(com\.)|(n(?=\s+\d)))\s*\*{0,2}\s*/iu;

/**
 * Numéro ARABE : lettre de tête optionnelle, entier, suffixe FR, sous-numéros `.2` / `-1`.
 *
 * La lettre de tête (`A1`, `B2ter`) n'est PAS décorative : un cabinet prescrit ce gabarit dans
 * son modèle de référence (cf. pattern 6/10 de l'historique `isPiecesLine`). Elle doit être
 * UNIQUE et COLLÉE au chiffre — `A 1` ou `AB1` ne sont pas des numéros de pièce, et laisser
 * passer plusieurs lettres rouvrirait la porte que la classe fermée de qualificatifs verrouille.
 *
 * Le lookahead `(?![\p{L}\d])` est load-bearing : sans lui, `Pièce 1er` / `1ère` / `2ème`
 * passent (mutation D : 6 tests rouges).
 */
const ARABIC_RE = new RegExp(
  `^(?:([A-Za-z])-?)?(\\d{1,4})\\s*(${FR_SUFFIXES.join('|')})?((?:[.\\-]\\d{1,3})*)(?![\\p{L}\\d])`,
  'iu',
);

// ─── Numéros romains ────────────────────────────────────────────────────────
// COPIE ANNOTÉE de `packages/piece-number-sort/src/romanSections.ts` (alphabet, table de
// ré-encodage, plafond). À GARDER ALIGNÉE avec cette source.
//
// WHY une copie et pas un import : `@hector/shared-types` n'a que `zod` en dépendance, et
// `apps/addin-word` — qui consomme `isPiecesLine` — ne dépend PAS de `@hector/piece-number-sort`.
// Ajouter la dépendance ici la rendrait irrésolvable au build webpack de l'add-in et imposerait
// de toucher les COPY du Dockerfile backend : hors périmètre, pour 15 lignes.
//
// L'alphabet est borné à `[IVX]` — `L C D M` sont EXCLUS volontairement. `C` et `D` sont des
// chiffres romains valides (100, 500) ET des marqueurs de section légitimes ; mesure prod
// 2026-07-08 : l'alphabet complet produit 144 détections dont 140 fausses (`D`×67, `C`×51).
// `[IVX]` couvre `I..XXXIX`, très au-delà du besoin d'un bordereau.

/** Table de ré-encodage (décroissante), bornée à l'alphabet `[IVX]`. */
const ROMAN_TABLE: ReadonlyArray<readonly [number, string]> = [
  [10, 'X'],
  [9, 'IX'],
  [5, 'V'],
  [4, 'IV'],
  [1, 'I'],
];

/** `XXXIX` = 39, plafond atteignable avec `[IVX]` seuls. */
const MAX_ROMAN = 39;

/** Jeton romain + sous-numéro optionnel (`I-1`, `II.3`), sans lettre collée derrière. */
const ROMAN_RE = /^([IVX]{1,9})((?:[.-]\d{1,3})*)(?![\p{L}\d])/iu;

/**
 * Convertit un chiffre romain **canonique** en entier, ou `null`.
 *
 * Strict par ré-encodage : `IIII`, `VV`, `IXI`, `VX` se « calculent » mais ne sont pas la forme
 * canonique — sans cette validation, `IIII` (=4) se confondrait avec `IV`.
 */
function romanToInt(token: string): number | null {
  const upper = token.toUpperCase();
  let total = 0;
  let rest = upper;
  for (const [value, symbol] of ROMAN_TABLE) {
    while (rest.startsWith(symbol)) {
      total += value;
      rest = rest.slice(symbol.length);
    }
  }
  if (rest !== '' || total === 0 || total > MAX_ROMAN) return null;
  // Ré-encodage : seule la forme canonique survit.
  let round = '';
  let left = total;
  for (const [value, symbol] of ROMAN_TABLE) {
    while (left >= value) {
      round += symbol;
      left -= value;
    }
  }
  return round === upper ? total : null;
}

/** Séparateurs de liste. `à`/`a` ouvre une PLAGE ; les autres ajoutent un item. */
const SEP_RE = /^(?:(,|;|&|et|and)|(à|a))(?![\p{L}])\s*\*{0,2}\s*/iu;

/**
 * Plafond de développement d'une plage `Pièces X à Y`.
 *
 * WHY : `Pièces 1 à 2024` (une année prise pour une borne) ferait exploser le tableau en mémoire
 * chez tous les consommateurs. Au-delà, on conserve les DEUX bornes littérales et on lève
 * `rangeTruncated` — le consommateur décide (afficher la plage telle quelle plutôt que 2024 liens).
 */
export const MAX_RANGE_SPAN = 100;

/**
 * Nombre maximal de chiffres d'un numéro cité SANS signe `n°`.
 *
 * WHY (mesuré sur le dépôt, 2026-09-03) : la forme nue `Pièce 4, 20241114` existe dans des
 * contextes RAG réels — `20241114` est une DATE compactée, pas une pièce n°20 244 114. Avec un
 * signe explicite (`Pièce n°1234`) l'avocat a dit « numéro », on accepte jusqu'à 4 chiffres.
 * Sans signe, on plafonne à 3 : aucun bordereau réel du corpus ne dépasse 999 pièces.
 */
export const MAX_BARE_NUMBER_DIGITS = 3;

/** Un numéro porté par une citation. */
export interface PieceCitationNumber {
  /** Le numéro tel qu'écrit (`'3'`, `'3bis'`, `'1.2'`, `'9-1'`, `'A1bis'`, `'IV'`). */
  readonly raw: string;
  /** Famille du numéro. `'romain'` n'est jamais développé en plage (cf. {@link expandRange}). */
  readonly kind: 'arabe' | 'romain';
  /** Entier : entier de tête pour l'arabe (`3` pour `'3bis'`), valeur du jeton pour le romain (`'IV'` → 4). */
  readonly base: number;
  /** Lettre de tête majuscule (`'A'` pour `'A1bis'`), `''` si aucune. Arabe uniquement. */
  readonly prefix: string;
  /** Jeton romain canonique majuscule (`'IV'`), `null` si le numéro est arabe. */
  readonly roman: string | null;
  /** Suffixe FR normalisé minuscule (`'bis'`), `''` si aucun. */
  readonly suffix: string;
  /** Sous-numéros (`'1.2'` → `[2]`, `'9-1'` → `[1]`), `[]` si aucun. */
  readonly sub: readonly number[];
  /** `true` si ce numéro a été DÉDUIT d'une plage `X à Y` (donc non écrit littéralement). */
  readonly fromRange: boolean;
}

/** Une citation de pièce reconnue dans un texte. */
export interface PieceCitation {
  /** Le texte exact reconnu, tel qu'il apparaît dans l'entrée normalisée NFC. */
  readonly raw: string;
  /** Index de début dans `input.normalize('NFC')` (⚠️ pas dans `input` si l'entrée est NFD). */
  readonly start: number;
  /** Index de fin (exclu) dans `input.normalize('NFC')`. */
  readonly end: number;
  /** `true` si le mot de tête est au pluriel (`Pièces`). */
  readonly plural: boolean;
  /** Qualificatif reconnu, `null` si la citation est directe (`Pièce n°3`). */
  readonly qualifier: PieceCitationQualifier | null;
  /** Forme du signe de numéro en tête de liste, `null` si citation nue. */
  readonly sign: PieceNumberSign | null;
  /** `true` si le signe est répété devant chaque numéro (`Pièce n°1 & n°2`). */
  readonly signRepeated: boolean;
  /** Les numéros cités, plages développées, dans l'ordre d'écriture. Jamais vide. */
  readonly numbers: readonly PieceCitationNumber[];
  /** `true` si une plage a dépassé {@link MAX_RANGE_SPAN} et n'a PAS été développée. */
  readonly rangeTruncated: boolean;
}

/** Style d'écriture cible pour {@link normalizePieceCitations}. */
export interface PieceCitationStyle {
  /** Écrire `Pièce n°1` (`true`) ou `Pièce 1` nu (`false`). */
  readonly numeroSign: boolean;
  /** Séparateur du DERNIER item d'une liste. Défaut `'et'`. */
  readonly lastSeparator?: 'et' | '&';
  /** Regrouper 3 numéros consécutifs ou plus en plage `Pièces 1 à 5`. Défaut `false`. */
  readonly collapseRanges?: boolean;
}

/** Convertit une chaîne en NFC — obligatoire avant toute regex portant sur `è`. */
function toNfc(input: string): string {
  return input.normalize('NFC');
}

/** Avance sur les espaces (y compris insécables : `\s` couvre U+00A0, U+202F, U+2009). */
function skipSpaces(text: string, from: number): number {
  let i = from;
  while (i < text.length && /\s/.test(text.charAt(i))) i += 1;
  return i;
}

/** Lit un signe de numéro à `from`. Ne consomme rien si absent. */
function readSign(text: string, from: number): { sign: PieceNumberSign; next: number } | null {
  const m = SIGN_RE.exec(text.slice(from));
  if (!m) return null;
  // Ordre calé sur SIGN_RE. `m[6]` = « n » nu suivi d'un chiffre : c'est un `n°` dont l'OCR a
  // perdu le rond (relevé 18 fois : « Pièce n 1 : procès-verbal de bornage »). Classé `'no'`
  // faute de mieux — la nature du signe ne sert qu'à la lecture, jamais au rendu, que
  // `formatPieceCitation` décide depuis le style du cabinet.
  const sign: PieceNumberSign =
    m[1] ? 'degre' : m[2] ? 'no' : m[3] ? 'numero' : m[4] ? 'numero' : m[5] ? 'diese' : 'no';
  // `m[6]` = « com. » (« pièce adverse com. 9 »), `m[7]` = « n » nu : deux abréviations qui
  // valent signe de numéro. Toutes deux retombent sur `'no'` ci-dessus — la nature du signe
  // ne sert qu'à la lecture, le rendu venant du style du cabinet.
  return { sign, next: from + m[0].length };
}

/** Découpe la queue de sous-numéros (`'.2-1'` → `[2, 1]`). */
function readSubNumbers(tail: string): number[] {
  return tail
    .split(/[.-]/)
    .filter((s) => s !== '')
    .map((s) => parseInt(s, 10));
}

/**
 * Lit un numéro de pièce à `from`. `signed` autorise 4 chiffres (cf. {@link MAX_BARE_NUMBER_DIGITS}).
 *
 * L'ARABE est essayé EN PREMIER, exactement comme `parsePieceNumber` de
 * `@hector/piece-number-sort` (« l'arabe garde la priorité, `2-1` reste numeric ») : sans cet
 * ordre, `X1` serait lu comme le romain `X` suivi d'un `1` orphelin.
 */
function readNumber(
  text: string,
  from: number,
  signed: boolean,
): { number: PieceCitationNumber; next: number } | null {
  const slice = text.slice(from);

  const a = ARABIC_RE.exec(slice);
  if (a?.[2]) {
    const digits = a[2];
    // Garde anti-date/anti-année : sans « n° » explicite, un nombre long n'est pas un n° de pièce.
    if (!signed && digits.length > MAX_BARE_NUMBER_DIGITS) return null;
    const base = parseInt(digits, 10);
    // Invariant produit partagé avec renumber-pieces : un acte ne cite JAMAIS de « Pièce n°0 ».
    if (base <= 0) return null;
    const raw = text.slice(from, from + a[0].length).trim();
    return {
      number: {
        raw,
        kind: 'arabe',
        base,
        prefix: (a[1] ?? '').toUpperCase(),
        roman: null,
        suffix: (a[3] ?? '').toLowerCase(),
        sub: readSubNumbers(a[4] ?? ''),
        fromRange: false,
      },
      next: from + a[0].length,
    };
  }

  const r = ROMAN_RE.exec(slice);
  if (r?.[1]) {
    // Rejette les formes non canoniques (`IIII`, `VV`) et tout ce qui dépasse XXXIX.
    const value = romanToInt(r[1]);
    if (value === null) return null;
    const raw = text.slice(from, from + r[0].length).trim();
    return {
      number: {
        raw,
        kind: 'romain',
        base: value,
        prefix: '',
        roman: r[1].toUpperCase(),
        suffix: '',
        sub: readSubNumbers(r[2] ?? ''),
        fromRange: false,
      },
      next: from + r[0].length,
    };
  }

  return null;
}

/** Développe une plage `from → to` en numéros entiers simples. */
function expandRange(from: PieceCitationNumber, to: PieceCitationNumber): PieceCitationNumber[] | null {
  // Une plage n'a de sens qu'entre deux entiers ARABES simples, nus et croissants.
  // Un romain (`Pièces I à IV`) ou une lettre de tête (`Pièces A1 à A4`) restent littéraux :
  // développer supposerait une arithmétique de gabarit que ce module n'a pas à inventer.
  if (from.kind !== 'arabe' || to.kind !== 'arabe') return null;
  if (from.prefix !== '' || to.prefix !== '') return null;
  if (from.suffix !== '' || to.suffix !== '' || from.sub.length > 0 || to.sub.length > 0) return null;
  if (to.base <= from.base) return null;
  if (to.base - from.base > MAX_RANGE_SPAN) return null;
  const out: PieceCitationNumber[] = [];
  for (let n = from.base + 1; n <= to.base; n += 1) {
    out.push({
      raw: String(n),
      kind: 'arabe',
      base: n,
      prefix: '',
      roman: null,
      suffix: '',
      sub: [],
      fromRange: n !== to.base,
    });
  }
  return out;
}

/**
 * Reconnaît TOUTES les citations de pièces d'un texte.
 *
 * Fonction pure. L'entrée est normalisée NFC en interne (piège macOS/NFD documenté dans
 * `@hector/piece-number-sort`) : **les offsets `start`/`end` sont relatifs à
 * `text.normalize('NFC')`**, pas à la chaîne d'origine si celle-ci est décomposée. Utilisez
 * {@link normalizeForPieceCitations} en amont si vous conservez les offsets.
 *
 * @param text Texte d'acte (corps, ligne, ou paragraphe).
 * @returns Les citations dans l'ordre d'apparition. Tableau vide si aucune.
 *
 * @example
 * parsePieceCitations('Voir Pièces n° 1, 2 et 3.').flatMap(c => c.numbers.map(n => n.raw));
 * // ['1', '2', '3']
 * parsePieceCitations('sa pièce d'identité'); // [] — faux positif écarté
 */
export function parsePieceCitations(text: string): PieceCitation[] {
  if (typeof text !== 'string' || text === '') return [];
  const src = toNfc(text);
  const out: PieceCitation[] = [];
  HEAD_RE.lastIndex = 0;
  let head: RegExpExecArray | null;
  while ((head = HEAD_RE.exec(src)) !== null) {
    // `start` exclut le gras markdown capturé : le renvoi commence au mot « Pièce ».
    const start = head.index + (head[1] ?? '').length;
    const plural = head[3] !== undefined;
    let i = head.index + head[0].length;

    // Le gras peut aussi fermer juste après le mot (`**Pièces** n°1`).
    i = skipSpaces(src, i);
    const closing = /^\*{0,2}/.exec(src.slice(i));
    i = skipSpaces(src, i + (closing?.[0].length ?? 0));

    // Qualificatif fermé — garde anti-faux-positif n°1.
    let qualifier: PieceCitationQualifier | null = null;
    const tail = src.slice(i);
    for (const q of QUALIFIERS) {
      const qm = q.re.exec(tail);
      if (qm) {
        qualifier = q.kind;
        i = skipSpaces(src, i + qm[0].length);
        break;
      }
    }

    let first = readSign(src, i);
    // Nom de partie ou d'enseigne glissé entre « pièce » et le signe : « Pièce YACCO n°5 »,
    // « Pièce EXPEDIPRO n°1 » — relevé 15 fois, des avocats qui étiquettent leurs pièces par
    // la partie qui les produit.
    //
    // C'est la SEULE entorse à la classe fermée de qualificatifs, et elle ne l'ouvre pas :
    // le jeton n'est sauté que si un SIGNE DE NUMÉRO le suit immédiatement. « pièce
    // d'identité », « pièce maîtresse », « pièce à conviction » n'ont pas de `n°` derrière et
    // restent donc rejetés — c'est le signe, pas le mot, qui autorise le saut. Un seul jeton,
    // sans espace : « Pièce du dossier n°3 » ne passe pas, et c'est voulu, `du dossier` n'est
    // pas une étiquette de pièce.
    if (!first) {
      const nom = /^[\p{L}][\p{L}\-']*/u.exec(src.slice(i));
      if (nom) {
        const apres = skipSpaces(src, i + nom[0].length);
        const signeApres = readSign(src, apres);
        if (signeApres) {
          i = apres;
          first = signeApres;
        }
      }
    }
    if (first) i = first.next;
    const sign = first?.sign ?? null;

    const firstNumber = readNumber(src, i, sign !== null);
    // Pas de numéro derrière « pièce » ⇒ ce n'est pas un renvoi (« pièce maîtresse »,
    // « pièces jointes : », « pièce d'identité »). On repart du prochain « pièce ».
    if (!firstNumber) continue;
    i = firstNumber.next;

    const numbers: PieceCitationNumber[] = [firstNumber.number];
    let signRepeated = false;
    let rangeTruncated = false;

    // Boucle de liste : `, 2`, `et 3`, `& n°4`, `à 7`.
    for (;;) {
      const afterSpaces = skipSpaces(src, i);
      const sep = SEP_RE.exec(src.slice(afterSpaces));
      if (!sep) break;
      let j = afterSpaces + sep[0].length;
      const isRange = sep[2] !== undefined;

      const itemSign = readSign(src, j);
      if (itemSign) {
        j = itemSign.next;
        signRepeated = true;
      }
      const item = readNumber(src, j, sign !== null || itemSign !== null);
      // Séparateur sans numéro derrière ⇒ la citation s'arrête AVANT le séparateur
      // (« Pièces 1 et 2 et le contrat » ne mange pas « et le contrat »).
      if (!item) break;

      if (isRange) {
        const expanded = expandRange(numbers[numbers.length - 1] as PieceCitationNumber, item.number);
        if (expanded) {
          numbers.push(...expanded);
        } else {
          // Plage invalide ou trop large : on garde les deux bornes littérales.
          numbers.push(item.number);
          rangeTruncated = true;
        }
      } else {
        numbers.push(item.number);
      }
      i = item.next;
    }

    out.push({
      raw: src.slice(start, i),
      start,
      end: i,
      plural,
      qualifier,
      sign,
      signRepeated,
      numbers,
      rangeTruncated,
    });
    // Reprendre APRÈS la citation évite de re-matcher un « pièce » consommé.
    HEAD_RE.lastIndex = i;
  }
  return out;
}

/**
 * Normalise une chaîne pour la reconnaissance de citations (NFC).
 *
 * À appeler en amont quand l'appelant a besoin que les offsets de {@link parsePieceCitations}
 * indexent SA chaîne (cas des noms de fichiers macOS, livrés en NFD par Finder/APFS).
 */
export function normalizeForPieceCitations(text: string): string {
  return toNfc(text);
}

/**
 * La chaîne EST-ELLE, à elle seule, une citation de pièce ? (ligne dédiée d'un bordereau ou
 * d'un corps d'acte). Tolère les parenthèses, le gras markdown, et une description après `:`.
 *
 * @example
 * startsWithPieceCitation('(Pièce n°3)');            // citation
 * startsWithPieceCitation('Pièce n°3 : Contrat');    // citation
 * startsWithPieceCitation('La pièce n°3 est claire');// null — ne DÉBUTE pas la ligne
 */
export function startsWithPieceCitation(text: string): PieceCitation | null {
  if (typeof text !== 'string') return null;
  const src = toNfc(text).trim().replace(/^[([«"'\s*#>-]+/u, '');
  const offset = toNfc(text).indexOf(src);
  const found = parsePieceCitations(src)[0];
  if (!found || found.start !== 0) return null;
  return offset > 0
    ? { ...found, start: found.start + offset, end: found.end + offset }
    : found;
}

/**
 * Tous les numéros de pièces cités dans un texte, dédoublonnés, dans l'ordre d'apparition.
 *
 * Remplace, côté consommateur, les boucles `PIECE_PATTERNS` mono-forme : couvre `Pièce 1` nu,
 * les listes, `&`, et les plages développées.
 *
 * @example
 * pieceNumbersFromText('Pièces 1 à 3, puis Pièce n°3 et Pièce N°7 & 8');
 * // ['1', '2', '3', '7', '8']
 */
export function pieceNumbersFromText(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const citation of parsePieceCitations(text)) {
    for (const n of citation.numbers) {
      const key = `${n.kind}|${n.prefix}|${n.base}|${n.suffix}|${n.sub.join('.')}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(n.raw);
    }
  }
  return out;
}

/** Découpe une liste de numéros en tranches consécutives (`1,2,3,7` → `[[1,2,3],[7]]`). */
function consecutiveRuns(numbers: readonly PieceCitationNumber[]): PieceCitationNumber[][] {
  const runs: PieceCitationNumber[][] = [];
  for (const n of numbers) {
    const last = runs[runs.length - 1];
    const prev = last?.[last.length - 1];
    const simple = n.kind === 'arabe' && n.prefix === '' && n.suffix === '' && n.sub.length === 0;
    const prevSimple = prev?.kind === 'arabe' && prev.prefix === '' && prev.suffix === '' && prev.sub.length === 0;
    if (last && prev && simple && prevSimple && n.base === prev.base + 1) {
      last.push(n);
    } else {
      runs.push([n]);
    }
  }
  return runs;
}

/**
 * Rend une citation dans le style demandé.
 *
 * @throws {RangeError} si la citation ne porte aucun numéro — invariant de
 *   {@link parsePieceCitations}, une citation sans numéro n'existe pas ; l'objet a été fabriqué
 *   à la main ou muté.
 *
 * @example
 * formatPieceCitation(cit, { numeroSign: false });                    // 'Pièces 1, 2 et 3'
 * formatPieceCitation(cit, { numeroSign: true, collapseRanges: true });// 'Pièces n°1 à 5'
 */
export function formatPieceCitation(citation: PieceCitation, style: PieceCitationStyle): string {
  if (citation.numbers.length === 0) {
    throw new RangeError(
      `formatPieceCitation: citation sans numéro (raw=${JSON.stringify(citation.raw)}, start=${citation.start}). ` +
        'Une PieceCitation produite par parsePieceCitations porte toujours au moins un numéro — ' +
        "vérifiez que l'objet n'a pas été construit ou muté à la main.",
    );
  }
  const lastSep = style.lastSeparator ?? 'et';
  const groups = style.collapseRanges === true ? consecutiveRuns(citation.numbers) : citation.numbers.map((n) => [n]);
  // Une suite de 3+ consécutifs devient UN jeton de plage ; en deçà, chaque numéro reste un jeton
  // à part pour que le séparateur final (« et » / « & ») s'applique (« Pièces 1 et 2 », pas « 1, 2 »).
  const tokens = groups.flatMap((run) =>
    run.length >= 3
      ? [`${(run[0] as PieceCitationNumber).raw} à ${(run[run.length - 1] as PieceCitationNumber).raw}`]
      : run.map((n) => n.raw),
  );
  let list: string;
  if (tokens.length === 1) {
    list = tokens[0] as string;
  } else {
    list = `${tokens.slice(0, -1).join(', ')} ${lastSep} ${tokens[tokens.length - 1] as string}`;
  }
  // Le pluriel suit le RENDU, pas la source : « Pièces 1 à 5 » compte 1 token mais 5 pièces.
  const word = citation.numbers.length > 1 ? 'Pièces' : 'Pièce';
  const qualifierWord = citation.qualifier === null ? '' : `${QUALIFIER_WORDS[citation.qualifier]} `;
  const signWord = style.numeroSign ? 'n°' : '';
  return `${word} ${qualifierWord}${signWord}${list}`;
}

/** Rendu français des qualificatifs (accord au pluriel géré par le consommateur si besoin). */
const QUALIFIER_WORDS: Record<PieceCitationQualifier, string> = {
  adverse: 'adverse',
  jointe: 'jointe',
  annexe: 'annexe',
  communiquee: 'communiquée',
  commune: 'commune',
  versee: 'versée',
  produite: 'produite',
};

/**
 * Réécrit TOUTES les citations d'un texte dans un style unique, en laissant le reste intact.
 *
 * C'est le point d'entrée « normalisation à la génération » : un acte produit par le LLM cite
 * `Pièce N°1 & 2` ici et `pièces n° 1, 2` là ; le cabinet, lui, a UNE convention (mesurée par
 * `pieceCitationMetrics`). On aligne sans toucher au reste de la prose.
 *
 * @example
 * normalizePieceCitations('Voir Pièce N°1 & 2 puis pièces 4 à 6.', { numeroSign: false });
 * // 'Voir Pièces 1 et 2 puis Pièces 4, 5 et 6.'
 */
export function normalizePieceCitations(text: string, style: PieceCitationStyle): string {
  if (typeof text !== 'string' || text === '') return '';
  const src = toNfc(text);
  const citations = parsePieceCitations(src);
  if (citations.length === 0) return src;
  let out = '';
  let cursor = 0;
  for (const citation of citations) {
    out += src.slice(cursor, citation.start) + formatPieceCitation(citation, style);
    cursor = citation.end;
  }
  return out + src.slice(cursor);
}
